/**
 * URL helpers for values that reach Publica.
 *
 * `businessWebsite` becomes the Publica line item's Adomain (see
 * selfservice-worker/src/processor/steps/ensureLineItems.ts). Adomain is the
 * OpenRTB advertiser domain, an identifier used for brand-safety matching, not
 * a click destination. Publica rejects these characters outright:
 *
 *   400 - line item adomain contains one or more prohibited characters: ", ', <, >, &
 *
 * A tracking-tagged URL (`?utm_source=X&utm_medium=Y`) therefore fails the
 * whole line item push. Since CTV-734 the wizard's step 1 and the Settings
 * Business tab reject such a value with a validation error
 * (`getAdomainUrlError`) instead of rewriting it under the buyer: they see what
 * is wrong and fix it. Per-creative click-through URLs are a separate field
 * where `&` is legal and expected, so click attribution is unaffected.
 * `sanitizeAdomain` in selfservice-worker/src/shared/adomain.ts stays as the
 * silent backstop for rows stored before CTV-734 and for direct API clients.
 *
 * Keep in lockstep with frontend/src/lib/urlValidation.ts. The shared subset is
 * exactly `PUBLICA_FORBIDDEN_ADOMAIN_CHARS`, `findForbiddenAdomainChars`,
 * `ADOMAIN_URL_ERRORS`, and `getAdomainUrlError`; this lib has no `isValidUrl`.
 */

export const PUBLICA_FORBIDDEN_ADOMAIN_CHARS = ['"', "'", '<', '>', '&'] as const;

/** Forbidden characters present in `value`, in declaration order, de-duplicated. */
export const findForbiddenAdomainChars = (value: string): string[] =>
  PUBLICA_FORBIDDEN_ADOMAIN_CHARS.filter((char) => value.includes(char));

/**
 * One shared string per case; the inputs and their tests both read from here.
 * The tracking message carries non-breaking spaces (U+00A0) inside "? or #"
 * and "(e.g. example.com)" so a narrow column never splits the two characters
 * the user has to look for, or leaves "(e.g." dangling on its own line.
 */
export const ADOMAIN_URL_ERRORS = {
  tracking:
    'Remove tracking parameters: enter the website address only, without anything from ?\u00a0or\u00a0# onward (e.g.\u00a0example.com).',
  forbiddenChars: 'Website address can\'t contain " \' < > &.',
} as const;

/**
 * The validation message for a value Publica would not accept as an Adomain
 * untouched, or null when it would. Tracking is checked first on purpose: a URL
 * like `https://x.com/?a=1&b=2` breaks both rules, and the tracking message is
 * the one that tells the user what to do. Empty input is null (required-ness
 * lives with the caller). Surrounding whitespace is ignored.
 */
export const getAdomainUrlError = (raw: string): string | null => {
  const value = (raw ?? '').trim();
  if (!value) return null;
  if (value.includes('?') || value.includes('#')) return ADOMAIN_URL_ERRORS.tracking;
  if (findForbiddenAdomainChars(value).length > 0) return ADOMAIN_URL_ERRORS.forbiddenChars;
  return null;
};

/**
 * Percent-encode the characters that are never legitimate unencoded in a URL.
 * Deliberately leaves `&` alone: click-through URLs need it for UTM parameters,
 * and Publica accepts it there.
 */
export const encodeUnsafeUrlChars = (value: string): string =>
  value
    .replace(/"/g, '%22')
    .replace(/'/g, '%27')
    .replace(/</g, '%3C')
    .replace(/>/g, '%3E');
